// import iconcus from "../../../assets/images/kirkos_logo.svg";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  kirkos_logo_black_yellow,
  car_sign_in,
  customer_lock_icon,
  kirkos_logo_white_yellow
} from "../../../assets/images";
import { CustomInput, ErrorModal, SuccessModal, Turnstile } from "../../../components";
import { TurnstileRef } from "../../../components/Turnstile";
import { useForm } from "react-hook-form";
import rules from "../../../rules";
import { useMutation } from "@tanstack/react-query";
import { trackEvent } from "../../../services/events";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { UserData } from "../../../types";
import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import "../signin/styles.css";
import "../signin/SignInScreen.css";
import "./forgotpassword.css";
import { ERROR_MESSAGES } from "../../../utils/messages";
import DraggableWhatsAppIcon from "../../../components/CustomerDashBoard/DraggableWhatsAppIcon";

const CustomerForgotPassword = () => {
  const phoneInputRef = useRef<HTMLInputElement>(null);
  // const [phoneNum, setPhoneNum] = useState<any>("");
  const [transactionId, setTransactionId] = useState<number | undefined>(0);
  // const [showOtpscreen, setShowOtpscreen] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false); // State to manage modal visibility
  const [errorMessage, setErrorMessage] = useState(""); // State to store error message
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);
  const forgotPasswordFormDataRef = useRef<any>(null);
  const cachedTokenRef = useRef<{ token: string; timestamp: number } | null>(null);

  const { state } = useLocation();
  const phoneNo = state && state.phoneNo;

  const navigate = useNavigate();

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { isValid, errors },
    setValue,
    trigger,
    getValues,
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    const trackPageLoad = async () => {
      await trackEvent({
        event_name: {
          web: "Kirkos_ForgetPasswordScreen",
          app: "Kirkos_ForgetPasswordScreen_App",
        },
        event_category: "SignIn",
        module: "Login",
        payload: {},
      });
    };
    trackPageLoad();
  }, []);

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    cachedTokenRef.current = { token, timestamp: Date.now() };

    const data = forgotPasswordFormDataRef.current;
    if (data) {
      const userData = {
        phoneNo: data.phoneNo,
        userType: "CONSUMER",
        event: "forgot_password",
        turnstile_response: turnstileToken,
      };
      mutation.mutate(userData);
      forgotPasswordFormDataRef.current = null;
    }
  };

  const onHandleSubmit = (data: any) => {
    forgotPasswordFormDataRef.current = data;
    setTurnstileToken(null);
    
    // Check if we have a cached token that's less than 5 minutes old
    const now = Date.now();
    if (cachedTokenRef.current && (now - cachedTokenRef.current.timestamp < 5 * 60 * 1000)) {
      handleTurnstileSuccess(cachedTokenRef.current.token);
      return;
    }
    
    // Otherwise, execute fresh verification
    turnstileRef.current?.reset();
    turnstileRef.current?.execute();
  };

  const onHandleError = (errors: any) => {
    const firstError = Object.values(errors)[0] as any;
    if (firstError) {
      toast.error(firstError.message || "Please enter a valid phone number");
    }
  };

  const mutation = useMutation({
    mutationFn: (userData: UserData) =>
      axiosInstance.post(API_URL.SEND_OTP, userData),
    onSuccess: async (successResponse) => {
      if (successResponse.status && successResponse?.data?.transactionId) {
        setTransactionId(successResponse?.data?.transactionId);
        sessionStorage.setItem(
          "transactionId",
          successResponse?.data?.transactionId
        );

        navigate("/forgot/otpscreen", {
          state: {
            phoneNo: getValues("phoneNo"),
            transactionId: transactionId,
          },
        });
      }
    },
    onError: async (error: any) => {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      forgotPasswordFormDataRef.current = null;
      cachedTokenRef.current = null;
      setErrorMessage(error?.data?.message || ERROR_MESSAGES.DEFAULT_ERROR);
      setShowErrorModal(true);
    },
  });
  return (
    <div className="customer-signin-page">
      <div className="customer-signin-shell">
        {/* Left Side: Brand Panel */}
        <div className="customer-signin-brand-panel">
          <div className="customer-signin-brand-topbar">
            <Link to="/" className="customer-signin-logo-link" aria-label="Kirkos home">
              <img
                src={kirkos_logo_white_yellow}
                alt="Kirkos"
                className="customer-signin-logo"
              />
            </Link>
            <div className="customer-signin-brand-links">
              <Link to="/" className="customer-signin-brand-link">
                Home
              </Link>
              <Link to="/signin" className="customer-signin-brand-link">
                Login
              </Link>
            </div>
          </div>

          <div className="customer-signin-brand-content">
            <span className="customer-signin-eyebrow">Recovery</span>
            <h1 className="customer-signin-brand-title">
              Enjoy your Trips with our wheels
            </h1>
            <p className="customer-signin-brand-copy">
              Get ready to hit the road with confidence. Your next generation
              adventure begins here.
            </p>

            <div className="customer-signin-brand-highlights">
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">24/7</span>
                <span className="customer-signin-highlight-label">
                  Trip support when plans shift
                </span>
              </div>
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">Fast</span>
                <span className="customer-signin-highlight-label">
                  Secure password recovery
                </span>
              </div>
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">UAE</span>
                <span className="customer-signin-highlight-label">
                  Trusted mobility experience
                </span>
              </div>
            </div>

            <div className="customer-signin-car-stage">
              <div className="customer-signin-car-glow" />
              <img
                src={car_sign_in}
                className="customer-signin-car-image"
                alt="Kirkos premium rental car"
              />
            </div>
          </div>
        </div>

        {/* Right Side: Recovery Card */}
        <div className="customer-signin-card">
          <form onSubmit={handleFormSubmit(onHandleSubmit, onHandleError)}>
            <div className="customer-signin-card-header">
              <div className="customer-signin-card-icon-wrap">
                <img
                  src={customer_lock_icon}
                  className="customer-signin-card-icon"
                  alt="Security"
                />
              </div>
              <span className="customer-signin-card-kicker">Security</span>
              <h2 className="customer-signin-heading">Forgot Password</h2>
              <p className="customer-signin-subcopy">
                Don’t worry! Resetting your password is easy. Just type your
                registered Phone number below and we'll send you an OTP.
              </p>
              <div className="customer-signin-links-row">
                <span className="customer-signin-links-label">
                  Remembered your password?
                </span>
                <Link to="/signin" className="customer-signin-inline-link">
                  Login
                </Link>
              </div>
            </div>

            <div className="customer-signin-form-area">
              <div className="customer-signin-field-block">
                <label className="customer-signin-label">
                  Enter your phone number
                </label>
                <div
                  className="customer-signin-input-shell"
                  onClick={() => phoneInputRef.current?.focus()}
                >
                  <span className="customer-signin-country-code">
                    <img
                      src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDYwMCAzMDAiPgogIDxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDA3MzJmIi8+CiAgPHJlY3Qgd2lkdGg9IjYwMCIgaGVpZ2h0PSIxMDAiIHk9IjEwMCIgZmlsbD0iI2ZmZiIvPgogIDxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iMTAwIiB5PSIyMDAiIGZpbGw9IiMwMDAiLz4KICA8cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2ZmMDAwMCIvPgo8L3N2Zz4="
                      alt="UAE"
                      className="customer-signin-flag"
                    />
                    +971
                  </span>
                  <CustomInput
                    inputName="phoneNo"
                    inputType="tel"
                    className="customer-signin-input"
                    placeholder="Enter your number"
                    editable={true}
                    onInputChange={(value: string) => { }}
                    control={control}
                    maxLength={9}
                    rules={rules.AuthRules.phone_number}
                    inputRef={phoneInputRef}
                  />
                </div>
              </div>

              <div className="customer-signin-turnstile-container" style={{ display: "none" }}>
                <Turnstile
                  ref={turnstileRef}
                  sitekey={process.env.REACT_APP_TURNSTILE_SITEKEY || ""}
                  appearance="interaction-only"
                  execution="execute"
                  onSuccess={handleTurnstileSuccess}
                  onError={() => {
                    setTurnstileToken(null);
                    forgotPasswordFormDataRef.current = null;
                    toast.error("Security verification failed. Please try again.");
                  }}
                  onExpire={() => {
                    setTurnstileToken(null);
                    forgotPasswordFormDataRef.current = null;
                    toast.warning("Security verification expired. Please verify again.");
                  }}
                />
              </div>

              <button
                className="customer-signin-submit mt-3"
                type="submit"
              >
                SEND OTP
              </button>

              <div className="customer-signin-support-strip">
                <span className="customer-signin-support-title">
                  Need help with access?
                </span>
                <span className="customer-signin-support-copy">
                  Our team can help you recover your account and continue your
                  booking without disruption.
                </span>
              </div>
            </div>
          </form>

          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            onNav={() => {
              navigate("/forgot/otpscreen", {
                state: {
                  phoneNo: getValues("phoneNo"),
                  transactionId: transactionId,
                },
              });
            }}
            message="Otp Send Success"
          />

          <ErrorModal
            isOpen={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            message={errorMessage || ERROR_MESSAGES.DEFAULT_ERROR}
          />
        </div>
      </div>
      <DraggableWhatsAppIcon />
    </div>
  );
};

export default CustomerForgotPassword;
